import React, { useEffect, useState } from "react";
import { api, Banner, Button, Field } from "./ui.tsx";
import type { VisitsAccess, VisitsList } from "../shared/visits.ts";
import { addressMapURL } from "../shared/maps.ts";
export function AddressMapLink({
  address,
}: {
  address: { street: string; number: string; neighborhood: string };
}) {
  return (
    <a
      className="button"
      href={addressMapURL(address)}
      target="_blank"
      rel="noopener noreferrer"
    >
      Abrir endereço no Google Maps ↗
    </a>
  );
}
type BoundaryData = {
  version: number;
  at?: string;
  spreadsheetId?: string;
  zones: Array<{ name: string; coordinates: number[][] }>;
};
function Boundaries({ admin }: { admin: boolean }) {
  const [data, setData] = useState<BoundaryData | null>(null),
    [group, setGroup] = useState(""),
    [subgroup, setSubgroup] = useState(""),
    [source, setSource] = useState(""),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  async function load() {
    const value = await api<BoundaryData>("/visits-geography");
    setData(value);
    setSource(value.spreadsheetId ?? "");
  }
  useEffect(() => {
    load().catch((e) => setError(e.message));
  }, []);
  const groupOf = (name: string) => name.replace(/ [A-F]$/, "");
  const groups = [
    ...new Set((data?.zones ?? []).map((z) => groupOf(z.name))),
  ].sort((a, b) => a.localeCompare(b, "pt-BR", { numeric: true }));
  const subgroups = (data?.zones ?? [])
    .filter((z) => groupOf(z.name) === group && z.name !== group)
    .map((z) => z.name.slice(-1))
    .sort();
  const selected = group ? `${group}${subgroup ? ` - ${subgroup}` : ""}` : "";
  useEffect(() => {
    if (data && !data.zones.some((z) => groupOf(z.name) === group)) {
      setGroup("");
      setSubgroup("");
    } else if (
      data &&
      subgroup &&
      !data.zones.some((z) => z.name === `${group} ${subgroup}`)
    )
      setSubgroup("");
  }, [data, group, subgroup]);
  const bounds = (data?.zones ?? []).reduce(
    (b, z) =>
      z.coordinates.reduce(
        (b, p) => [
          Math.min(b[0], p[0]),
          Math.max(b[1], p[0]),
          Math.min(b[2], p[1]),
          Math.max(b[3], p[1]),
        ],
        b,
      ),
    [Infinity, -Infinity, Infinity, -Infinity],
  );
  const [minX, maxX, minY, maxY] = bounds,
    scale = Math.min(
      760 / (maxX - minX || 1),
      460 / ((maxY - minY) * 1.09 || 1),
    );
  return (
    <section className="vo-surface">
      <h2>Limites dos grupos</h2>
      {error && <Banner kind="danger">{error}</Banner>}
      {!data && !error && (
        <p role="status">Carregando referência geográfica…</p>
      )}
      {data?.zones.length ? (
        <>
          <Field label="Grupo">
            <select
              value={group}
              onChange={(e) => {
                setGroup(e.target.value);
                setSubgroup("");
              }}
            >
              <option value="">Todos os grupos</option>
              {groups.map((name) => (
                <option key={name}>{name}</option>
              ))}
            </select>
          </Field>
          {group && subgroups.length > 0 && (
            <Field label="Subgrupo">
              <select
                value={subgroup}
                onChange={(e) => setSubgroup(e.target.value)}
              >
                <option value="">Todos os subgrupos</option>
                {subgroups.map((letter) => (
                  <option key={letter} value={letter}>
                    {letter}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <svg
            viewBox="0 0 800 500"
            style={{
              display: "block",
              width: "100%",
              height: "auto",
              background: "var(--surface,white)",
              borderRadius: 12,
            }}
            role="img"
            aria-label={
              selected
                ? `Limites de ${selected}`
                : "Contornos geográficos dos grupos da referência"
            }
          >
            {data.zones.map((z, i) => {
              const highlighted =
                groupOf(z.name) === group &&
                (!subgroup || z.name === `${group} ${subgroup}`);
              return (
                <polygon
                  key={z.name}
                  points={z.coordinates
                    .map(
                      (p) =>
                        `${20 + (p[0] - minX) * scale},${480 - (p[1] - minY) * 1.09 * scale}`,
                    )
                    .join(" ")}
                  fill={
                    highlighted
                      ? "var(--accent,#087d78)"
                      : `hsl(${170 + i * 13} 30% 65%)`
                  }
                  fillOpacity={group && !highlighted ? 0.12 : 0.65}
                  stroke="currentColor"
                  strokeWidth={highlighted ? 2 : 0.5}
                >
                  <title>{z.name}</title>
                </polygon>
              );
            })}
          </svg>
          <p className="small muted">
            {data.zones.length} contornos · Atualizado em{" "}
            {new Date(data.at!).toLocaleString("pt-BR")}. Somente os limites
            existentes na aba GRUPOS_GEOJSON são apresentados.
          </p>
          <p>
            A definição geográfica usa somente esses contornos. Endereços sem
            coordenada confiável continuam aguardando validação.
          </p>
        </>
      ) : (
        data && <p>Nenhuma referência geográfica publicada.</p>
      )}
      {admin && (
        <details>
          <summary>Configurar referência geográfica</summary>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              setError("");
              try {
                const id =
                  /\/spreadsheets\/d\/([\w-]+)/.exec(source)?.[1] ??
                  source.trim();
                await api("/visits-geography/import", "POST", {
                  spreadsheetId: id,
                  expectedVersion: data?.version ?? 0,
                });
                await load();
              } catch (e) {
                setError((e as Error).message);
              } finally {
                setBusy(false);
              }
            }}
          >
            <Field label="Planilha de limites dos grupos">
              <input
                value={source}
                onChange={(e) => setSource(e.target.value)}
                required
                placeholder="Cole o endereço da planilha"
              />
            </Field>
            <p>
              Leitura somente da aba GRUPOS_GEOJSON. A planilha não será
              modificada. Uma falha preserva a referência anterior.
            </p>
            <Button type="submit" busy={busy}>
              Atualizar referência
            </Button>
          </form>
        </details>
      )}
    </section>
  );
}
export function Maps({
  access,
  admin = false,
}: {
  access: VisitsAccess | null;
  admin?: boolean;
}) {
  const [search, setSearch] = useState(""),
    [query, setQuery] = useState(""),
    [page, setPage] = useState(1),
    [data, setData] = useState<VisitsList | null>(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!access?.permissions.records || !query.trim()) return;
    const controller = new AbortController();
    setBusy(true);
    setError("");
    setData(null);
    api<VisitsList>(
      "/visits-overview/records?" +
        new URLSearchParams({
          search: query,
          page: String(page),
          pageSize: "20",
        }),
      "GET",
      undefined,
      controller.signal,
    )
      .then(setData)
      .catch((e) => {
        if (!controller.signal.aborted) setError(e.message);
      })
      .finally(() => {
        if (!controller.signal.aborted) setBusy(false);
      });
    return () => controller.abort();
  }, [access, query, page, retry]);
  return (
    <section className="visits-overview">
      <h1>Mapas</h1>
      <p>Encontre um endereço da base e abra sua localização no mapa.</p>
      {!access?.permissions.records ? (
        <Banner>
          A consulta de endereços não foi autorizada para sua conta.
        </Banner>
      ) : (
        <>
          <form
            className="vo-surface"
            onSubmit={(e) => {
              e.preventDefault();
              setPage(1);
              setQuery(search.trim());
              setRetry((n) => n + 1);
            }}
          >
            <Field label="Buscar rua, número ou morador">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                required
                maxLength={240}
              />
            </Field>
            <Button type="submit" kind="primary" busy={busy}>
              Buscar endereço
            </Button>
          </form>
          <p className="small muted">
            O Google Maps só será aberto ao tocar no link, recebendo o endereço
            selecionado. O resultado da busca no mapa precisa ser conferido; ele
            não valida o grupo geográfico e não registra uma visita.
          </p>
          {busy && <p role="status">Buscando endereços…</p>}
          {error && (
            <Banner kind="danger">
              {error}{" "}
              <Button onClick={() => setRetry((n) => n + 1)}>
                Tentar novamente
              </Button>
            </Banner>
          )}
          {data && (
            <>
              <p>{data.total.toLocaleString("pt-BR")} endereços encontrados</p>
              <ul className="vo-list">
                {data.items.map((record) => (
                  <li className="vo-surface" key={record.id}>
                    <h2>
                      {record.street}, {record.number}
                    </h2>
                    <p>{record.neighborhood || "Bairro não informado"}</p>
                    <AddressMapLink address={record} />
                  </li>
                ))}
              </ul>
              {data.total > 20 && (
                <div>
                  <Button
                    disabled={page === 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Anterior
                  </Button>{" "}
                  <span>Página {page}</span>{" "}
                  <Button
                    disabled={page * 20 >= data.total}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Próxima
                  </Button>
                </div>
              )}
            </>
          )}
          <Boundaries admin={admin} />
        </>
      )}
    </section>
  );
}
